import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Upload,
  X,
  Home,
  Building,
  Search,
  MapPin,
  Camera,
  User,
  Check,
  Loader2,
  Baby,
  Dice5,
  Building2,
  Trees,
  Film,
  Droplet,
  Sun,
  ScanLine,
  Flame,
  Dumbbell,
  Droplets,
  Car,
  Warehouse,
  Landmark,
  Lightbulb,
  Gamepad2,
  Bike,
  CheckCircle,
  CloudRain,
  ShieldCheck,
  BatteryCharging,
  FileText,
} from "lucide-react";
import Cookies from "js-cookie";
import apiClient from "../common/apiClient";
import { useNavigate } from "react-router-dom";
import { AppHeader } from "./AppHeader";
import ProBrokerFooter from "./ProBrokerFooter";
import {
  useUserProfile,
  getCityOptions,
  withSelectedCity,
} from "../contexts/UserProfileContext";

// Add this component before the main component
const ResponsePopup = ({ message, type, onClose }) => {
  const getDisplayMessage = () => {
    if (typeof message === "string") {
      return message;
    }
    if (typeof message === "object") {
      return message.error || message.message || JSON.stringify(message);
    }
    return "Unknown error occurred";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div
          className={`text-center mb-4 ${
            type === "success" ? "text-green-600" : "text-red-600"
          }`}
        >
          <div className="text-6xl mb-4">
            {type === "success" ? "✅" : "❌"}
          </div>
          <h3 className="text-xl font-bold mb-2">
            {type === "success" ? "Success!" : "Error!"}
          </h3>
          <div className="text-gray-600">{getDisplayMessage()}</div>
        </div>
        <div className="mt-6 flex justify-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const LoadingPopup = ({ message = "Submitting..." }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 max-w-sm w-full mx-4 shadow-2xl">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Please Wait</h3>
          <p className="text-gray-600">{message}</p>
        </div>
      </div>
    </div>
  );
};

const initialFormData = {
  listingType: "",
  type: "",
  priceMin: "",
  priceMax: "",
  price: "", // Single price for broker listings
  priceOnRequest: false,
  bhk: [],
  sqFt: "",
  sqYd: "",
  sqFtMax: "",
  subType: "",
  societyName: "",
  projectName: "",
  area: "",
  areas: [],
  city: "",
  furnishing: [],
  amenities: [],
  unitsAvailable: "",
  description: "",
  brokerName: "",
  builderName: "",
  developerName: "",
  contactNumber: "",
  images: [],
  title: "",
  occupencyType: "",
  age: "",
  floors: "",
  block: "",
  parking: "",
  parkingType: "",
  parkingCharge: "",
  basicRate: "",
  dpRate: "",
  facing: "",
  otherFacing: "",
  possessionDate: "",
  launchDate: "",
  reraId: "",
  totalUnit: "",
  bathrooms: "",
  tenant: "",
  // Remove unitType from here
  requirementData: {
    priceRange: {
      min: "",
      max: "",
    },
    sizeRange: {
      sqFt: {
        min: "",
        max: "",
      },
      sqYd: {
        min: "",
        max: "",
      },
    },
    areas: [],
    bhk: [],
    furnishingPreferences: [],
  },
};

const categories = [
  {
    id: "Residential Rent",
    label: "Residential Rent",
    icon: Home,
    color: "from-blue-500 to-blue-600",
  },
  {
    id: "Residential Sell",
    label: "Residential Sell",
    icon: Home,
    color: "from-green-500 to-green-600",
  },
  {
    id: "Commercial Rent",
    label: "Commercial Rent",
    icon: Building,
    color: "from-purple-500 to-purple-600",
  },
  {
    id: "Commercial Sell",
    label: "Commercial Sell",
    icon: Building,
    color: "from-orange-500 to-orange-600",
  },
];

const commercialSubtypes = [
  "Basement",
  "Commercial Plot",
  "Shed",
  "Co Working Space",
  "Factory",
  "Shop",
  "Commercial Building",
  "Godown",
  "Showroom",
  "Restaurant",
  "Industrial Land",
  "Commercial Flat",
  "Office",
  "Ware House",
  "Pre Leased Spaces",
  "Commericial Bunglow",
  "Land",
  "Agriculture Land",
  "PG House",
  "Hotel",
  "Gym",
  "Spa",
  "Hospital",
  "Salon",
  "Petrol Pump",
  "Cafe",
  "PreSchool",
  "Industrial Plot",
  "Industrial Shed",
];

const residentialSubtypes = [
  "Apartment",
  "Bungalow",
  "Tenement",
  "Penthouse",
  "Weekend Home",
  "PG",
  "Rowhouse",
  "Residential Plot",
  "Pre Leased Spaces",
];
const AMENITIES = [
  { id: "Children's Play Area", icon: Baby },
  { id: "Board Games", icon: Dice5 },
  { id: "Lift(s)", icon: Building2 },
  { id: "Landscaping & Trees", icon: Trees },
  { id: "Mini Theatre", icon: Film },
  { id: "RO Water System", icon: Droplet },
  { id: "Solar Power System", icon: Sun },
  { id: "Sensor Operated Lights", icon: ScanLine },
  { id: "Fire Fighting System", icon: Flame },
  { id: "Gymnasium", icon: Dumbbell },
  { id: "24x7 Water Supply", icon: Droplets },
  { id: "Car Parking", icon: Car },
  { id: "Multipurpose Hall", icon: Warehouse },
  { id: "Community Hall", icon: Landmark },
  { id: "Street Lighting", icon: Lightbulb },
  { id: "Indoor Games", icon: Gamepad2 },
  { id: "Cycling & Jogging Track", icon: Bike },
  { id: "Internal Roads & Footpaths", icon: CheckCircle },
  { id: "Rain Water Harvesting", icon: CloudRain },
  { id: "24x7 Security", icon: ShieldCheck },
  { id: "Power Backup", icon: BatteryCharging },
];

const bhk = [
  "Studio Apartment",
  "1 BHK",
  "2 BHK",
  "3 BHK",
  "4 BHK",
  "5 BHK",
  "6 BHK",
  "6+ BHK",
];
const residentialFurnishingOptions = [
  "Fully Furnished",
  "Furnished",
  "Semi-Furnished",
  "Kitchen-Fix",
  "Unfurnished",
];

export default function PropertyListed() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  // Cities the logged-in user has access to (PropertyAccessCity). The live
  // profile wins once it loads; the cookie covers the render before that.
  const { propertyAccessCities } = useUserProfile();
  const cityOptions = withSelectedCity(
    propertyAccessCities?.length ? propertyAccessCities : getCityOptions(),
    formData.city
  );
  const [dragActivePhotos, setDragActivePhotos] = useState(false);
  const [dragActiveBrochures, setDragActiveBrochures] = useState(false);
  const [areaInput, setAreaInput] = useState("");
  const [areaUnit, setAreaUnit] = useState("sqFt"); // Default to sqFt
  const [responseMessage, setResponseMessage] = useState("");
  const [responseType, setResponseType] = useState("success"); // or 'error'
  const [isResponseOpen, setIsResponseOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState({
    message: "",
    type: "success",
  });
  const [areaSuggestions, setAreaSuggestions] = useState([]);
  const [isFetchingAreas, setIsFetchingAreas] = useState(false);
  const [activeAreaField, setActiveAreaField] = useState(null); // 'requirement' | 'listing' | null
  const [cityAreas, setCityAreas] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add validation states for price and size ranges
  const [priceRangeError, setPriceRangeError] = useState("");
  const [sizeRangeError, setSizeRangeError] = useState("");

  const navigate = useNavigate();

  const steps =
    formData.listingType === "builders"
      ? [
          { id: "basics", title: "Basics", icon: Home },
          { id: "details", title: "Details", icon: MapPin },
          { id: "amenities", title: "Amenities", icon: Check },
          { id: "contact", title: "Contact & Media", icon: User },
        ]
      : [
          { id: "basics", title: "Basics", icon: Home },
          { id: "details", title: "Details", icon: MapPin },
          { id: "contact", title: "Contact & Media", icon: User },
        ];
  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Auto-navigate when type is selected
  // useEffect(() => {
  //   if (currentStep === 0 && formData.listingType && formData.type) {
  //     setTimeout(() => {
  //       setCurrentStep(1);
  //     }, 500);
  //   }
  // }, [formData.listingType, formData.type, currentStep]);

  const getFurnishingOptions = (propertyType) => {
    if (
      propertyType.includes("Residential") ||
      propertyType.includes("Commercial")
    ) {
      return residentialFurnishingOptions;
    }
    return [];
  };

  const sanitizeDescription = (text) => {
    if (!text) return "";

    let sanitized = text;

    // Remove emojis and special symbols
    sanitized = sanitized.replace(
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
      ""
    );

    // Remove URLs with various protocols
    sanitized = sanitized.replace(/(https?|ftp|file):\/\/[^\s]+/g, "");

    // Remove email addresses
    // sanitized = sanitized.replace(/\S+@\S+\.\S+/g, '');

    // Remove www links
    sanitized = sanitized.replace(/www\.\S+/g, "");

    // Remove common domain patterns
    // sanitized = sanitized.replace(/\S+\.(com|org|net|io|co|in)\S*/g, '');

    // Trim extra spaces created by removals
    // sanitized = sanitized.replace(/\s+/g, ' ').trim();

    return sanitized;
  };
  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleArrayToggle = (field, value) => {
    const currentArray = formData[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter((item) => item !== value)
      : [...currentArray, value];
    updateFormData(field, newArray);
  };

  const handleImageUpload = (
    files,
    allowedCategories = ["image", "video", "document"]
  ) => {
    const allowedTypesMap = {
      image: ["image/jpeg", "image/png", "image/gif"],
      video: ["video/mp4", "video/quicktime"],
      document: ["application/pdf"],
    };

    const validFiles = Array.from(files).filter((file) => {
      const isValidType = allowedCategories.some(
        (cat) =>
          allowedTypesMap[cat] && allowedTypesMap[cat].includes(file.type)
      );
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      return isValidType && isValidSize;
    });

    if (validFiles.length < files.length) {
      const types = allowedCategories.join(", ");
      alert(`Some files were skipped. Only ${types} under 10MB are allowed.`);
    }

    // Create media objects with preview URLs
    const newMedia = validFiles.map((file) => ({
      file: file,
      type: Object.keys(allowedTypesMap).find((key) =>
        allowedTypesMap[key].includes(file.type)
      ),
      preview: URL.createObjectURL(file),
      name: file.name,
    }));

    updateFormData("images", [...formData.images, ...newMedia]);
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    updateFormData("images", newImages);
  };

  const handleDrag = (e, setDragActiveState) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveState(true);
    } else if (e.type === "dragleave") {
      setDragActiveState(false);
    }
  };

  const handleDrop = (e, setDragActiveState, categories) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveState(false);
    if (e.dataTransfer.files) {
      handleImageUpload(e.dataTransfer.files, categories);
    }
  };

  const getModuleType = () => {
    if (formData.listingType === "broker") return "broker";
    if (formData.listingType === "requirement") return "requirement";
    if (formData.listingType === "builders") return "builder";
    return "owner";
  };

  // Areas of the selected city, loaded from the admin area list whenever the
  // city (or listing type) changes. Area suggestions are filtered from this.
  useEffect(() => {
    if (!formData.city) {
      setCityAreas([]);
      return;
    }

    let cancelled = false;
    const endpoint = `/ijscui/probroker-admin/ceknvuhbd/cjwbhusb/protected/admin/areas?city=${encodeURIComponent(
      formData.city
    )}&moduleType=${encodeURIComponent(getModuleType())}&limit=2000`;

    apiClient("GET", endpoint).then((response) => {
      if (cancelled) return;
      const payload = response?.data;
      const list =
        [payload?.data?.docs, payload?.data, payload?.docs, payload].find((c) =>
          Array.isArray(c)
        ) || [];

      const seen = new Set();
      const areas = list
        .map((item) => {
          const name =
            typeof item === "string"
              ? item
              : item?.areaName || item?.name || item?.area || "";
          return {
            name: String(name).trim(),
            _id: item?._id || item?.id || name,
          };
        })
        .filter((a) => {
          const key = a.name.toLowerCase();
          if (!a.name || seen.has(key)) return false;
          seen.add(key);
          return true;
        });

      setCityAreas(areas);
    });

    return () => {
      cancelled = true;
    };
  }, [formData.city, formData.listingType]);

  const handleCityChange = (value) => {
    setFormData((prev) => ({ ...prev, city: value, area: "" }));
    setAreaSuggestions([]);
  };

  const fetchAreaSuggestions = async (query) => {
    // City selected and its areas loaded: suggest from that list (all of it
    // when nothing is typed yet).
    if (cityAreas.length) {
      const q = (query || "").trim().toLowerCase();
      setAreaSuggestions(
        q ? cityAreas.filter((a) => a.name.toLowerCase().includes(q)) : cityAreas
      );
      return;
    }

    if (!query || !query.trim()) {
      setAreaSuggestions([]);
      return;
    }

    const moduleType = getModuleType();
    const endpoint = `/cjidnvij/ceksfbuebijn/user/areas/suggest?query=${encodeURIComponent(
      query.trim()
    )}&moduleType=${encodeURIComponent(moduleType)}&caseInsensitive=true`;

    try {
      setIsFetchingAreas(true);
      const response = await apiClient("GET", endpoint, null, {
        headers: { "Content-Type": "application/json" },
      });

      // Check if response has data property and handle both array and object responses
      if (response && response.data) {
        const suggestions = Array.isArray(response.data)
          ? response.data
          : response.data.data || [];

        // Map the response to ensure consistent object structure
        const formattedSuggestions = suggestions.map((item) => ({
          name: item.name || item.areaName || item,
          _id: item._id || item.id || item.name,
        }));

        setAreaSuggestions(formattedSuggestions);
      } else {
        setAreaSuggestions([]);
      }
    } catch (err) {
      console.error("Error fetching area suggestions:", err);
      setAreaSuggestions([]);
    } finally {
      setIsFetchingAreas(false);
    }
  };
  useEffect(() => {
    let debounceId;
    const query =
      activeAreaField === "requirement"
        ? areaInput
        : activeAreaField === "listing"
        ? formData.area
        : "";

    // console.log('useEffect - activeAreaField:', activeAreaField, 'query:', query); // DEBUG

    if (activeAreaField && cityAreas.length) {
      fetchAreaSuggestions(query);
    } else if (activeAreaField && query && query.length >= 1) {
      debounceId = setTimeout(() => {
        fetchAreaSuggestions(query);
      }, 300);
    } else {
      setAreaSuggestions([]);
    }
    return () => debounceId && clearTimeout(debounceId);
  }, [areaInput, formData.area, formData.listingType, activeAreaField, cityAreas]);

  const handleAddArea = () => {
    const areaToAdd = formData.area.trim();

    if (
      areaToAdd &&
      formData.requirementData.areas.length < 5 &&
      !formData.requirementData.areas.includes(areaToAdd)
    ) {
      setFormData((prev) => {
        const currentAreas = prev.requirementData.areas || [];
        return {
          ...prev,
          area: "", // Clear the input after adding
          requirementData: {
            ...prev.requirementData,
            areas: [...currentAreas, areaToAdd],
          },
        };
      });
      // Clear suggestions after manual add
      setAreaSuggestions([]);
    }
  };

  const handleSelectSuggestion = (name) => {
    if (activeAreaField === "requirement") {
      setFormData((prev) => {
        const current = prev.requirementData.areas || [];
        const currentLower = current.map((area) => area.toLowerCase());
        if (currentLower.includes(name.toLowerCase()) || current.length >= 5)
          return prev;
        return {
          ...prev,
          requirementData: {
            ...prev.requirementData,
            areas: [...current, name],
          },
        };
      });
      setAreaInput("");
    } else if (activeAreaField === "listing") {
      // Only set the area if it comes from suggestions
      if (areaSuggestions.some((item) => item.name === name)) {
        updateFormData("area", name);
      }
    }

    // Clear suggestions and active field after selection
    setAreaSuggestions([]);
    setActiveAreaField(null);
  };

  const isStepComplete = (stepIndex) => {
    const stepId = steps[stepIndex]?.id;

    switch (stepId) {
      case "basics":
        return formData.listingType !== "" && formData.type !== "";

      case "details":
        if (formData.listingType === "requirement") {
          // Updated validation - only min price is required, max is optional
          const priceValid =
            formData.requirementData.priceRange.min !== "" && !priceRangeError;

          const sizeValid = !sizeRangeError;

          return (
            formData.subType !== "" &&
            priceValid &&
            formData.city !== "" &&
            formData.requirementData.areas.length > 0 &&
            sizeValid
          );
        } else {
          return (
            formData.subType !== "" &&
            formData.area !== "" &&
            formData.city !== ""
          );
        }

      case "amenities":
        // Amenities step is always complete (optional selection)
        return true;

      case "contact":
        return (
          formData.contactNumber !== "" &&
          (formData.brokerName !== "" || formData.builderName !== "")
        );

      default:
        return false;
    }
  };

  const renderProgressBar = () => (
    <div className="flex items-center justify-center lg:ml-32 mb-8 px-1 md:max-w-3xl mx-auto">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = index === currentStep;
        const isCompleted = index < currentStep;

        return (
          <div key={step.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-3">
              <div
                className={`
              w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mb-2 transition-all duration-300
              ${
                isActive
                  ? "bg-[#503691] text-white shadow-lg scale-110"
                  : isCompleted
                  ? "bg-green-500 text-white"
                  : "bg-gray-200 text-gray-500"
              }
            `}
              >
                {isCompleted && !isActive ? (
                  <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </div>
              <span
                className={`text-xs sm:text-sm font-medium text-center ${
                  isActive
                    ? "text-indigo-600"
                    : isCompleted
                    ? "text-green-600"
                    : "text-gray-500"
                }`}
              >
                {step.title}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-2 sm:mx-4 transition-colors duration-300 ${
                  index < currentStep ? "bg-green-500" : "bg-gray-200"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  const renderBasicsStep = () => (
    <div className="space-y-6">
      {/* Listing Type Selection */}
      <div className="space-y-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
          What would you like to do?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {[
            {
              id: "broker",
              title: "List Property",
              desc: "Sell or rent",
              icon: Home,
              gradient: "from-blue-500 to-indigo-600",
            },
            {
              id: "requirement",
              title: "Post Requirement",
              desc: "Find properties",
              icon: Search,
              gradient: "from-green-500 to-emerald-600",
            },
            {
              id: "builders",
              title: "Builder Project",
              desc: "New projects",
              icon: Building,
              gradient: "from-purple-500 to-violet-600",
            },
          ].map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => updateFormData("listingType", type.id)}
                className={`p-4 sm:p-6 rounded-2xl border-2 transition-all duration-300 hover:scale-105 ${
                  formData.listingType === type.id
                    ? "border-transparent bg-gradient-to-br " +
                      type.gradient +
                      " text-white shadow-xl"
                    : "border-gray-200 hover:border-gray-300 bg-white hover:shadow-lg"
                }`}
              >
                <Icon className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 sm:mb-3" />
                <h3 className="text-sm sm:text-base font-semibold mb-1">
                  {type.title}
                </h3>
                <p className="text-xs sm:text-sm opacity-80">{type.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Type Selection */}
      {formData.listingType && (
        <div className="space-y-4 animate-in slide-in-from-bottom duration-500">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            Select type
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {formData.listingType === "builders" ? (
              // For Builder Project, show only Residential and Commercial
              <>
                <button
                  onClick={() => updateFormData("type", "Residential")}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 hover:scale-105 ${
                    formData.type === "Residential"
                      ? "border-transparent bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg"
                      : "border-gray-200 hover:border-gray-300 bg-white hover:shadow-md"
                  }`}
                >
                  <Home className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-2" />
                  <span className="text-sm sm:text-base font-medium">
                    Residential
                  </span>
                </button>
                <button
                  onClick={() => updateFormData("type", "Commercial")}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 hover:scale-105 ${
                    formData.type === "Commercial"
                      ? "border-transparent bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg"
                      : "border-gray-200 hover:border-gray-300 bg-white hover:shadow-md"
                  }`}
                >
                  <Building className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-2" />
                  <span className="text-sm sm:text-base font-medium">
                    Commercial
                  </span>
                </button>
              </>
            ) : (
              // For other types, show all categories
              categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => updateFormData("type", cat.id)}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 hover:scale-105 ${
                      formData.type === cat.id
                        ? "border-transparent bg-gradient-to-r " +
                          cat.color +
                          " text-white shadow-lg"
                        : "border-gray-200 hover:border-gray-300 bg-white hover:shadow-md"
                    }`}
                  >
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-2" />
                    <span className="text-sm sm:text-base font-medium">
                      {cat.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderDetailsStep = () => (
    <div className="space-y-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
        Property Details
      </h2>

      {formData.listingType === "requirement" ? (
        // Requirement specific form
        <div className="space-y-6">
          {/* Title and Property Type Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Title */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Title / Premise Name 
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Building className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    updateFormData("title", capitalizeWords(e.target.value))
                  }
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter Title / Premise Name"                
                />
              </div>
            </div>

            {/* Property Type */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Property Type *
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Home className="w-5 h-5" />
                </div>
                <select
                  value={formData.subType}
                  onChange={(e) => updateFormData("subType", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white transition-all"
                  required
                >
                  <option value="">Select Category</option>
                  {formData.type.includes("Commercial")
                    ? commercialSubtypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))
                    : residentialSubtypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Price Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min Price *
              </label>
              <input
                type="number"
                value={formData.requirementData.priceRange.min}
                onChange={(e) => {
                  const minValue = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    requirementData: {
                      ...prev.requirementData,
                      priceRange: {
                        ...prev.requirementData.priceRange,
                        min: minValue,
                      },
                    },
                  }));

                  // Validate price range when min changes (only if max is provided)
                  if (
                    minValue &&
                    formData.requirementData.priceRange.max &&
                    Number(minValue) >
                      Number(formData.requirementData.priceRange.max)
                  ) {
                    setPriceRangeError(
                      "Minimum price cannot be greater than maximum price"
                    );
                  } else {
                    setPriceRangeError("");
                  }
                }}
                className={`w-full px-4 py-3 border ${
                  priceRangeError ? "border-red-500" : "border-gray-300"
                } rounded-xl focus:outline-none focus:ring-2`}
                placeholder="₹ Enter minimum price"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Price
              </label>
              <input
                type="number"
                value={formData.requirementData.priceRange.max}
                onChange={(e) => {
                  const maxValue = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    requirementData: {
                      ...prev.requirementData,
                      priceRange: {
                        ...prev.requirementData.priceRange,
                        max: maxValue,
                      },
                    },
                  }));

                  // Validate price range when max changes (only if min is provided)
                  if (
                    maxValue &&
                    formData.requirementData.priceRange.min &&
                    Number(maxValue) <
                      Number(formData.requirementData.priceRange.min)
                  ) {
                    setPriceRangeError(
                      "Maximum price cannot be less than minimum price"
                    );
                  } else {
                    setPriceRangeError("");
                  }
                }}
                className={`w-full px-4 py-3 border ${
                  priceRangeError ? "border-red-500" : "border-gray-300"
                } rounded-xl focus:outline-none focus:ring-2`}
                placeholder="₹ Enter maximum price (optional)"
              />
            </div>
            {priceRangeError && (
              <div className="col-span-1 sm:col-span-2 text-red-500 text-sm mt-1">
                {priceRangeError}
              </div>
            )}
          </div>

          {/* Size Range with Unit Selection */}
          {/* // Update the Size Range section in the requirement form with this code: */}

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Size Range
            </label>
            <div className="flex">
              {/* Select Dropdown */}
              <select
                value={areaUnit}
                onChange={(e) => {
                  setAreaUnit(e.target.value);
                  // Clear the values of the previous unit type
                  setFormData((prev) => ({
                    ...prev,
                    requirementData: {
                      ...prev.requirementData,
                      sizeRange: {
                        ...prev.requirementData.sizeRange,
                        [e.target.value === "sqFt" ? "sqYd" : "sqFt"]: {
                          min: "",
                          max: "",
                        },
                      },
                    },
                  }));
                  // Clear size range error when changing units
                  setSizeRangeError("");
                }}
                className="px-4 py-3 border border-gray-300 rounded-s-xl focus:outline-none focus:ring-2 bg-white"
              >
                <option value="sqFt">SQFT</option>
                <option value="sqYd">SQYD</option>
              </select>
              <div className="gap-2 flex w-full">
                {/* Min Input */}
                <input
                  type="number"
                  value={
                    areaUnit === "sqFt"
                      ? formData.requirementData.sizeRange.sqFt.min
                      : formData.requirementData.sizeRange.sqYd.min
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      requirementData: {
                        ...prev.requirementData,
                        sizeRange: {
                          ...prev.requirementData.sizeRange,
                          [areaUnit === "sqFt" ? "sqFt" : "sqYd"]: {
                            ...prev.requirementData.sizeRange[
                              areaUnit === "sqFt" ? "sqFt" : "sqYd"
                            ],
                            min: value,
                          },
                        },
                      },
                    }));

                    // Validate size range when min changes (only if max is provided)
                    const currentMax =
                      areaUnit === "sqFt"
                        ? formData.requirementData.sizeRange.sqFt.max
                        : formData.requirementData.sizeRange.sqYd.max;

                    if (
                      value &&
                      currentMax &&
                      Number(value) > Number(currentMax)
                    ) {
                      setSizeRangeError(
                        "Minimum size cannot be greater than maximum size"
                      );
                    } else {
                      setSizeRangeError("");
                    }
                  }}
                  className={`flex-1 px-4 gap-3 py-3 border ${
                    sizeRangeError ? "border-red-500" : "border-gray-300"
                  } rounded-e-xl focus:outline-none focus:ring-2`}
                  placeholder={`Min Size (${areaUnit})`}
                />

                {/* Max Input - Optional */}
                <input
                  type="number"
                  value={
                    areaUnit === "sqFt"
                      ? formData.requirementData.sizeRange.sqFt.max
                      : formData.requirementData.sizeRange.sqYd.max
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      requirementData: {
                        ...prev.requirementData,
                        sizeRange: {
                          ...prev.requirementData.sizeRange,
                          [areaUnit === "sqFt" ? "sqFt" : "sqYd"]: {
                            ...prev.requirementData.sizeRange[
                              areaUnit === "sqFt" ? "sqFt" : "sqYd"
                            ],
                            max: value,
                          },
                        },
                      },
                    }));

                    // Validate size range when max changes (only if min is provided)
                    const currentMin =
                      areaUnit === "sqFt"
                        ? formData.requirementData.sizeRange.sqFt.min
                        : formData.requirementData.sizeRange.sqYd.min;

                    if (
                      value &&
                      currentMin &&
                      Number(value) < Number(currentMin)
                    ) {
                      setSizeRangeError(
                        "Maximum size cannot be less than minimum size"
                      );
                    } else {
                      setSizeRangeError("");
                    }
                  }}
                  className={`flex-1 px-4 py-3 border ${
                    sizeRangeError ? "border-red-500" : "border-gray-300"
                  } rounded-xl focus:outline-none focus:ring-2`}
                  placeholder={`Max Size (${areaUnit}) - optional`}
                />
              </div>
            </div>
            {sizeRangeError && (
              <div className="text-red-500 text-sm mt-1">{sizeRangeError}</div>
            )}
          </div>

          {/* City & Areas */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Preferred City & Areas (Max 5) *
            </label>
            <div className="flex flex-col sm:flex-row gap-3 items-end mb-3">
              <div className="flex-[1] w-full">
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  City *
                </label>
                <select
                  value={formData.city}
                  onChange={(e) => handleCityChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                  required
                >
                  <option value="">Select City</option>
                  {cityOptions.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 relative w-full">
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Area *
                </label>
                <input
                  type="text"
                  value={formData.area}
                  onChange={(e) => {
                    updateFormData("area", e.target.value);
                    // Automatically set activeAreaField when user starts typing
                    if (!activeAreaField) {
                      setActiveAreaField("listing");
                    }
                  }}
                  onFocus={() => {
                    console.log(
                      "🎯 Input focused, setting activeAreaField to listing"
                    );
                    setActiveAreaField("listing");
                  }}
                  onBlur={() => {
                    // Don't clear immediately, wait to see if user clicked a suggestion
                    setTimeout(() => {
                      // Only clear if no suggestion was selected
                      if (areaSuggestions.length === 0) {
                        setActiveAreaField(null);
                      }
                    }, 200);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddArea();
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2"
                  placeholder="Search for area..."
                  required
                />
                {activeAreaField === "listing" &&
                  areaSuggestions.length > 0 && (
                    <div className="absolute z-40 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                      {areaSuggestions.map((item) => (
                        <button
                          key={item._id || item.name}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectSuggestion(item.name);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-50"
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                {activeAreaField === "listing" && isFetchingAreas && (
                  <div className="absolute z-40 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-2 text-sm text-gray-500">
                    Searching…
                  </div>
                )}
              </div>

              {/* Add Button */}
              <div className="w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handleAddArea}
                  disabled={
                    !formData.area.trim() ||
                    formData.requirementData.areas.length >= 5 ||
                    formData.requirementData.areas.includes(
                      formData.area.trim()
                    )
                  }
                  className="px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Selected Areas Display */}
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.requirementData.areas.map((area, index) => (
                <div
                  key={index}
                  className="flex items-center bg-gray-100 px-3 py-1.5 rounded-full group hover:bg-gray-200"
                >
                  <span className="text-gray-700 text-sm">{area}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const newAreas = formData.requirementData.areas.filter(
                        (_, i) => i !== index
                      );
                      setFormData((prev) => ({
                        ...prev,
                        requirementData: {
                          ...prev.requirementData,
                          areas: newAreas,
                        },
                      }));
                    }}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Helper Text */}
            <p className="text-xs text-gray-500 mt-2">
              {formData.requirementData.areas.length}/5 areas selected
            </p>
          </div>

          {/* BHK Options */}
          {!formData.type.includes("Commercial") && (
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                BHK Configuration
              </label>
              <div className="flex flex-wrap gap-2">
                {bhk.map((bhk) => (
                  <button
                    key={bhk}
                    type="button"
                    onClick={() => {
                      const currentOptions = formData.requirementData.bhk;
                      const newOptions = currentOptions.includes(bhk)
                        ? currentOptions.filter((item) => item !== bhk)
                        : [...currentOptions, bhk];
                      setFormData((prev) => ({
                        ...prev,
                        requirementData: {
                          ...prev.requirementData,
                          bhk: newOptions,
                        },
                      }));
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      formData.requirementData.bhk.includes(bhk)
                        ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {bhk}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Furnishing Preferences */}
          {/* Furnishing Preferences - Show different options for Residential vs Commercial */}
          {(formData.type.includes("Residential") ||
            formData.type.includes("Commercial")) && (
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Furnishing Preferences
              </label>
              <div className="flex flex-wrap gap-2">
                {getFurnishingOptions(formData.type).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      const currentPrefs =
                        formData.requirementData.furnishingPreferences;
                      const newPrefs = currentPrefs.includes(option)
                        ? currentPrefs.filter((item) => item !== option)
                        : [...currentPrefs, option];
                      setFormData((prev) => ({
                        ...prev,
                        requirementData: {
                          ...prev.requirementData,
                          furnishingPreferences: newPrefs,
                        },
                      }));
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      formData.requirementData.furnishingPreferences.includes(
                        option
                      )
                        ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => {
                const sanitizedValue = sanitizeDescription(e.target.value);
                updateFormData("description", sanitizedValue);
              }}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 resize-none"
              placeholder="Enter additional details..."
            />
          </div>
        </div>
      ) : (
        // Updated Existing property listing form
        <div className="space-y-6">
          {/* Row A: Property Type, Price, sqFt */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Property Type *
              </label>
              <select
                value={formData.subType}
                onChange={(e) => updateFormData("subType", e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              >
                <option value="">Select Property Type</option>
                {formData.type.includes("Commercial")
                  ? // Filter options for builder commercial projects
                    formData.listingType === "builders" &&
                    formData.type === "Commercial"
                    ? commercialSubtypes
                        .filter((type) =>
                          [
                            "Shop",
                            "Showroom",
                            "Office",
                            "Industrial Land",
                            "Industrial Shed",
                          ].includes(type)
                        )
                        .map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))
                    : // Show all commercial subtypes for non-builder commercial
                      commercialSubtypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))
                  : // Filter options for builder residential projects
                  formData.listingType === "builders" &&
                    formData.type === "Residential"
                  ? residentialSubtypes
                      .filter((type) =>
                        [
                          "Apartment",
                          "Bungalow",
                          "Weekend Home",
                          "Rowhouse",
                          "Residential Plot",
                        ].includes(type)
                      )
                      .map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))
                  : // Show all residential subtypes for non-builder residential
                    residentialSubtypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
              </select>
            </div>

            {formData.listingType === "builders" ? (
              // Show price range for builders
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700">
                    Price Min *
                  </label>
                  <input
                    type="number"
                    value={formData.priceMin}
                    onChange={(e) => updateFormData("priceMin", e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2"
                    placeholder="₹ Enter minimum price"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700">
                    Price Max *
                  </label>
                  <input
                    type="number"
                    value={formData.priceMax}
                    onChange={(e) => updateFormData("priceMax", e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2"
                    placeholder="₹ Enter maximum price"
                    required
                  />
                </div>
              </div>
            ) : (
              // Show single price for broker
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  Price *
                </label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => updateFormData("price", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2"
                  placeholder="₹ Enter price"
                  required
                />
              </div>
            )}

            {/* Size Input with Unit Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700">
                Size
              </label>
              <div className="flex ">
                <select
                  value={areaUnit}
                  onChange={(e) => setAreaUnit(e.target.value)}
                  className="px-2 py-3 border border-gray-300 rounded-l-xl focus:outline-none focus:ring-2 bg-white"
                >
                  <option value="sqFt">SQFT</option>
                  <option value="sqYd">SQYD</option>
                </select>
                <input
                  type="number"
                  value={areaUnit === "sqFt" ? formData.sqFt : formData.sqYd}
                  onChange={(e) => {
                    if (areaUnit === "sqFt") {
                      updateFormData("sqFt", e.target.value);
                      updateFormData("sqYd", ""); // Clear other unit when one is selected
                    } else {
                      updateFormData("sqYd", e.target.value);
                      updateFormData("sqFt", ""); // Clear other unit when one is selected
                    }
                  }}
                  className="flex-1 px-1 w-10 py-3 border border-gray-300 rounded-r-xl focus:outline-none focus:ring-2"
                  placeholder={`Enter area in ${areaUnit.toUpperCase()}`}
                />
              </div>
            </div>
          </div>

          {/* Row B: Property Title, Area, City */}
          {formData.listingType !== "requirement" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  Building Name / Premise Name
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    updateFormData("title", capitalizeWords(e.target.value))
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2"
                  placeholder="Enter Building Name / Premise Name"
                  required
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700">
                  Area *
                </label>
                <input
                  type="text"
                  value={formData.area}
                  onChange={(e) => updateFormData("area", e.target.value)}
                  onFocus={() => setActiveAreaField("listing")}
                  onBlur={() => setTimeout(() => setActiveAreaField(null), 150)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2"
                  placeholder="Enter area"
                  required
                />
                {activeAreaField === "listing" &&
                  areaSuggestions.length > 0 && (
                    <div className="absolute z-40 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                      {areaSuggestions.map((item) => (
                        <button
                          key={item._id || item.name}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleSelectSuggestion(item.name)}
                          className="w-full text-left px-4 py-2 hover:bg-gray-50"
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                {activeAreaField === "listing" && isFetchingAreas && (
                  <div className="absolute z-40 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-2 text-sm text-gray-500">
                    Searching…
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  City *
                </label>
                {formData.listingType === "requirement" ? (
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => updateFormData("city", e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2"
                    placeholder="Enter city"
                    required
                  />
                ) : (
                  <select
                    value={formData.city}
                    onChange={(e) => handleCityChange(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                    required
                  >
                    <option value="">Select City</option>
                    {cityOptions.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}

          {/* Row C: Property Status, Occupancy Type */}
          {formData.listingType !== "requirement" && (
            <div className="grid grid-cols-3 gap-6 w-full">
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Nearby Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) =>
                    updateFormData("address", capitalizeWords(e.target.value))
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2"
                  placeholder="Enter complete Nearby Address"
                />
              </div>

              {/* Only show Occupancy Type for Residential properties */}
              {formData.type === "Residential Rent" && (
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-gray-700">
                    Occupancy Type
                  </label>
                  <select
                    value={formData.occupencyType}
                    onChange={(e) =>
                      updateFormData("occupencyType", e.target.value)
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                  >
                    <option value="Only For Family">Only For Family</option>
                    <option value="Only For Bachelors">
                      Only For Bachelors
                    </option>
                    <option value="Family-Bachelors">Family-Bachelors</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Row D: Age, Floor, Bathrooms */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700">
                Property Age
              </label>
              <select
                value={formData.age}
                onChange={(e) => updateFormData("age", e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
              >
                {formData.listingType === "builders" ? (
                  <>
                    <option value="">Select Option</option>
                    <option value="Upcoming Project">Upcoming Project</option>
                    <option value="Under Construction">
                      Under Construction
                    </option>
                    <option value="Ready to Move">Ready to Move</option>
                  </>
                ) : (
                  <>
                    <option value="0-1 year">Select Age</option>
                    <option value="1-5 years">1-5 Years</option>
                    <option value="5-10 years">5-10 Years</option>
                    <option value="10-15 years">10-15 Years</option>
                    <option value="15-20 years">15-20 Years</option>
                    <option value="20-25 years">20-25 Years</option>
                    <option value="25-30 years">25-30 Years</option>
                    <option value="30-35 years">30-35 Years</option>
                    <option value="35-40 years">35-40 Years</option>
                    <option value="40-45 years">40-45 Years</option>
                    <option value="45-50 years">45-50 Years</option>
                    <option value="50-above">50 & Above</option>
                    <option value="newly-constructed">Newly Constructed</option>
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700">
                Floor
              </label>
              <input
                type="number"
                value={formData.floors}
                onChange={(e) => updateFormData("floors", e.target.value)}
                placeholder="Enter Floor Number"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700">
                Washroom
              </label>
              <select
                value={formData.bathrooms}
                onChange={(e) => updateFormData("bathrooms", e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4+">4+</option>
              </select>
            </div>
            {formData.listingType === "builders" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  No. of Blocks
                </label>
                <input
                  type="text"
                  value={formData.block}
                  onChange={(e) => updateFormData("block", e.target.value)}
                  placeholder="Enter Block Number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                />
              </div>
            )}

            {formData.listingType === "builders" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  Possession Date
                </label>
                <input
                  type="date"
                  value={formData.possessionDate}
                  onChange={(e) =>
                    updateFormData("possessionDate", e.target.value)
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                />
              </div>
            )}
            {formData.listingType === "builders" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  Launch Date
                </label>
                <input
                  type="date"
                  value={formData.launchDate}
                  onChange={(e) => updateFormData("launchDate", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                />
              </div>
            )}
            {formData.listingType === "builders" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  RERA Id
                </label>
                <input
                  type="text"
                  value={formData.reraId}
                  onChange={(e) => updateFormData("reraId", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                />
              </div>
            )}
            {formData.listingType === "builders" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  Total Unit
                </label>
                <input
                  type="number"
                  value={formData.totalUnit}
                  onChange={(e) => updateFormData("totalUnit", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                />
              </div>
            )}
            {formData.listingType === "builders" &&
              formData.type?.includes("Residential") && (
                <div className="space-y-3">
                  {/* Facing Dropdown */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700">
                      FACING
                    </label>
                    <select
                      value={formData.facing || ""}
                      onChange={(e) => updateFormData("facing", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2"
                    >
                      <option value="">Select Facing</option>
                      <option value="Road">Road</option>
                      <option value="Garden">Garden</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Other Facing Input */}
                  {formData.facing === "Other" && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700">
                        OTHER FACING
                      </label>
                      <input
                        type="text"
                        value={formData.otherFacing || ""}
                        onChange={(e) =>
                          updateFormData("otherFacing", e.target.value)
                        }
                        placeholder="Enter facing details"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                      />
                    </div>
                  )}
                </div>
              )}

            {formData.listingType === "builders" &&
              formData.type.includes("Residential") && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700">
                    Type Of Parking
                  </label>
                  <select
                    value={formData.parkingType || ""}
                    onChange={(e) =>
                      updateFormData("parkingType", e.target.value)
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2"
                  >
                    <option value="">Select Parking Type</option>
                    <option value="Open">Open</option>
                    <option value="Covered">Covered</option>
                  </select>
                </div>
              )}

            {formData.listingType === "builders" &&
              formData.type.includes("Commercial") && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700">
                    Parking Charge
                  </label>
                  <input
                    type="text"
                    value={formData.parkingCharge}
                    onChange={(e) =>
                      updateFormData("parkingCharge", e.target.value)
                    }
                    placeholder="Enter Parking Charge"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                  />
                </div>
              )}
            {formData.listingType === "builders" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  No. of Parking
                </label>
                <input
                  type="text"
                  value={formData.parking}
                  onChange={(e) => updateFormData("parking", e.target.value)}
                  placeholder="Enter Number of Parking"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                />
              </div>
            )}

            {formData.listingType === "builders" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  BASIC RATE
                </label>
                <input
                  type="number"
                  value={formData.basicRate}
                  onChange={(e) => updateFormData("basicRate", e.target.value)}
                  placeholder="Enter Basic Rate"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                />
              </div>
            )}
            {formData.listingType === "builders" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  DP RATE
                </label>
                <input
                  type="number"
                  value={formData.dpRate}
                  onChange={(e) => updateFormData("dpRate", e.target.value)}
                  placeholder="Enter DP Rate"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                />
              </div>
            )}
          </div>

          {/* // Add this after the Bathrooms select and before the Tenant section in renderDetailsStep(): */}

          {/* Only show BHK Configuration for Residential properties */}
          {/* Only show BHK Configuration for Residential properties */}
          {!formData.type.includes("Commercial") &&
            formData.listingType !== "requirement" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  BHK Configuration
                </label>
                <div className="flex flex-wrap gap-2">
                  {bhk
                    .filter((option) => {
                      // For builder projects, show all options including Studio and Apartment
                      if (formData.listingType === "builders") {
                        return true;
                      }
                      // For non-builder projects, exclude Studio and Apartment
                      return option !== "Studio" && option !== "Apartment";
                    })
                    .map((bhk) => (
                      <button
                        key={bhk}
                        type="button"
                        onClick={() => {
                          if (formData.listingType === "builders") {
                            // Builders can select multiple BHK options
                            const currentBhk = formData.bhk || [];
                            const newBhk = currentBhk.includes(bhk)
                              ? currentBhk.filter((item) => item !== bhk)
                              : [...currentBhk, bhk];
                            updateFormData("bhk", newBhk);
                          } else {
                            // Brokers can select only one BHK option
                            updateFormData("bhk", [bhk]);
                          }
                        }}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                          formData.bhk.includes(bhk)
                            ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        {bhk}
                      </button>
                    ))}
                </div>
                {formData.listingType === "builders" && (
                  <p className="text-xs text-gray-500 mt-2">
                    You can select multiple BHK configurations for builder
                    projects
                  </p>
                )}
              </div>
            )}

          {/* Row E: Tenant Preference and Remark */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.type.includes("rent") && (
              <div>
                <label className="block text-sm font-semibold text-gray-700">
                  Tenant Preference
                </label>
                <select
                  value={formData.tenant}
                  onChange={(e) => updateFormData("tenant", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 bg-white"
                >
                  <option value="">No Preference</option>
                  <option value="Family">Family Only</option>
                  <option value="Bachelor">Bachelor Only</option>
                  <option value="Company">Company Only</option>
                </select>
              </div>
            )}
          </div>

          {/* Furnishing Preferences */}
          {/* Furnishing Preferences - Show different options for Residential vs Commercial */}
          {(formData.type.includes("Residential") ||
            formData.type.includes("Commercial")) && (
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                Furnishing Type
              </label>
              <div className="flex flex-wrap gap-2">
                {getFurnishingOptions(formData.type).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      // single select: stored as array with one item
                      updateFormData("furnishing", [option]);
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      formData.furnishing.includes(option)
                        ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Row F: Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => {
                const sanitizedValue = sanitizeDescription(e.target.value);
                updateFormData("description", sanitizedValue);
              }}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 resize-none"
              placeholder="Enter additional details..."
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderAmenitiesStep = () => (
    <div className="space-y-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Amenities</h2>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
        {AMENITIES.map(({ id, icon: Icon }) => {
          const selected = formData.amenities.includes(id);

          return (
            <button
              key={id}
              type="button"
              onClick={() => {
                const updated = selected
                  ? formData.amenities.filter((a) => a !== id)
                  : [...formData.amenities, id];
                updateFormData("amenities", updated);
              }}
              className={`flex flex-col items-center gap-3 p-3 rounded-xl border transition-all
              ${
                selected
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <Icon size={26} strokeWidth={1.5} />
              <span className="text-sm text-center leading-tight">{id}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderContactStep = () => (
    <div className="space-y-10">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
        Contact & Media
      </h2>

      {/* Contact Information */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 sm:p-6 rounded-2xl space-y-4">
        <h3 className="font-semibold text-gray-900">Contact Information</h3>

        {/* Sales Agent Name - Only for Builders */}
        {formData.listingType === "builders" && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sales Agent Name *
            </label>
            <input
              type="text"
              value={formData.builderName || ""}
              onChange={(e) =>
                updateFormData("builderName", capitalizeWords(e.target.value))
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter sales agent name"
              required
              minLength={2}
            />
            {formData.builderName && formData.builderName.length < 2 && (
              <p className="text-red-500 text-xs mt-1">
                Name must be at least 2 characters
              </p>
            )}
          </div>
        )}

        {/* Developer Name - Only for Builders */}
        {formData.listingType === "builders" && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Developer Name *
            </label>
            <input
              type="text"
              value={formData.developerName || ""}
              onChange={(e) =>
                updateFormData("developerName", capitalizeWords(e.target.value))
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter developer name"
              required
              minLength={2}
            />
            {formData.developerName && formData.developerName.length < 2 && (
              <p className="text-red-500 text-xs mt-1">
                Name must be at least 2 characters
              </p>
            )}
          </div>
        )}

        {/* Broker Name - Only for non-Builders */}
        {formData.listingType !== "builders" && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Broker Name *
            </label>
            <input
              type="text"
              value={formData.brokerName || ""}
              onChange={(e) =>
                updateFormData("brokerName", capitalizeWords(e.target.value))
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter broker name"
              required
              minLength={2}
            />
            {formData.brokerName && formData.brokerName.length < 2 && (
              <p className="text-red-500 text-xs mt-1">
                Name must be at least 2 characters
              </p>
            )}
          </div>
        )}

        {/* Contact Number - Common for all */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Contact Number *
          </label>
          <input
            type="tel"
            value={formData.contactNumber || ""}
            onChange={(e) => {
              // Allow only numbers and limit to 10 digits
              const value = e.target.value.replace(/\D/g, "").slice(0, 10);
              updateFormData("contactNumber", value);
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="Enter 10-digit contact number"
            required
            maxLength={10}
            pattern="[0-9]{10}"
          />
          {formData.contactNumber && formData.contactNumber.length !== 10 && (
            <p className="text-red-500 text-xs mt-1">
              Contact number must be exactly 10 digits
            </p>
          )}
        </div>
      </div>

      {/* Media Upload - Only show for non-requirement listings */}
      {formData.listingType !== "requirement" && (
        <div className="space-y-8">
          {/* Photos & Videos Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Add Photos & Videos</h3>
            <div
              className={`border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center transition-all duration-300 ${
                dragActivePhotos
                  ? "border-indigo-500 bg-indigo-50"
                  : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"
              }`}
              onDragEnter={(e) => handleDrag(e, setDragActivePhotos)}
              onDragLeave={(e) => handleDrag(e, setDragActivePhotos)}
              onDragOver={(e) => handleDrag(e, setDragActivePhotos)}
              onDrop={(e) =>
                handleDrop(e, setDragActivePhotos, ["image", "video"])
              }
            >
              <Camera className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-base sm:text-lg font-medium text-gray-700 mb-2">
                Drag and drop your photos/videos here
              </p>
              <p className="text-gray-500 mb-4">or</p>
              <label className="inline-block bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-medium cursor-pointer transition-all duration-300 transform hover:scale-105">
                Choose Files
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={(e) =>
                    e.target.files &&
                    handleImageUpload(e.target.files, ["image", "video"])
                  }
                  className="hidden"
                />
              </label>
              <p className="text-xs text-gray-500 mt-3">
                Supports: JPG, PNG, MP4, Max 10MB each
              </p>
            </div>

            {formData.images.some(
              (item) => item.type === "image" || item.type === "video"
            ) && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {formData.images.map((mediaItem, index) => {
                  if (mediaItem.type === "document") return null;
                  return (
                    <div key={index} className="relative group">
                      <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
                        {mediaItem.type === "image" ? (
                          <img
                            src={mediaItem.preview}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <video
                            src={mediaItem.preview}
                            className="w-full h-full object-cover"
                            controls
                          />
                        )}
                      </div>
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-red-600"
                      >
                        <X className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Brochures Section (Only for builders) */}
          {formData.listingType === "builders" && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">
                Add Brochures (PDF)
              </h3>
              <div
                className={`border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center transition-all duration-300 ${
                  dragActiveBrochures
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-gray-300 hover:border-indigo-400 hover:bg-gray-50"
                }`}
                onDragEnter={(e) => handleDrag(e, setDragActiveBrochures)}
                onDragLeave={(e) => handleDrag(e, setDragActiveBrochures)}
                onDragOver={(e) => handleDrag(e, setDragActiveBrochures)}
                onDrop={(e) =>
                  handleDrop(e, setDragActiveBrochures, ["document"])
                }
              >
                <FileText className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-base sm:text-lg font-medium text-gray-700 mb-2">
                  Drag and drop your brochures here
                </p>
                <p className="text-gray-500 mb-4">or</p>
                <label className="inline-block bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-medium cursor-pointer transition-all duration-300 transform hover:scale-105">
                  Choose PDF
                  <input
                    type="file"
                    multiple
                    accept=".pdf"
                    onChange={(e) =>
                      e.target.files &&
                      handleImageUpload(e.target.files, ["document"])
                    }
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-3">
                  Supports: PDF, Max 10MB each
                </p>
              </div>

              {formData.images.some((item) => item.type === "document") && (
                <div className="grid grid-cols-1 gap-3">
                  {formData.images.map((mediaItem, index) => {
                    if (mediaItem.type !== "document") return null;
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200"
                      >
                        <div className="flex items-center space-x-3 overflow-hidden">
                          <div className="bg-red-100 p-2 rounded-lg">
                            <FileText className="w-6 h-6 text-red-500" />
                          </div>
                          <span className="text-sm font-medium text-gray-700 truncate">
                            {mediaItem.name}
                          </span>
                        </div>
                        <button
                          onClick={() => removeImage(index)}
                          className="text-gray-400 hover:text-red-500 transition-colors p-2"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (steps[currentStep]?.id) {
      case "basics":
        return renderBasicsStep();
      case "details":
        return renderDetailsStep();
      case "amenities":
        return renderAmenitiesStep(); // ✅ NEW
      case "contact":
        return renderContactStep();
      default:
        return null;
    }
  };

  const validateForm = () => {
    // Get the appropriate name based on listing type
    const name =
      formData.listingType === "builders"
        ? formData.builderName
        : formData.brokerName;

    const contactNumber = formData.contactNumber;

    // Name validation
    if (!name || name.trim().length === 0) {
      setPopupMessage({
        type: "error",
        message: `${
          formData.listingType === "builders" ? "Builder Name" : "Broker Name"
        } is required.`,
      });
      setShowPopup(true);
      return false;
    }

    // Name length validation
    if (name.trim().length < 2) {
      setPopupMessage({
        type: "error",
        message: `${
          formData.listingType === "builders" ? "Builder Name" : "Broker Name"
        } must be at least 2 characters long.`,
      });
      setShowPopup(true);
      return false;
    }

    // Phone number validation
    if (!contactNumber || contactNumber.trim().length === 0) {
      setPopupMessage({
        type: "error",
        message: "Contact number is required.",
      });
      setShowPopup(true);
      return false;
    }

    // Phone number format validation (10 digits)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(contactNumber.replace(/\D/g, ""))) {
      setPopupMessage({
        type: "error",
        message: "Please enter a valid 10-digit contact number.",
      });
      setShowPopup(true);
      return false;
    }

    // City validation
    if (!formData.city || formData.city.trim().length === 0) {
      setPopupMessage({
        type: "error",
        message: "City is required.",
      });
      setShowPopup(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Client-side validation
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    try {
      const token = Cookies.get("token");
      if (!token) {
        setPopupMessage({
          type: "error",
          message: "Unauthorized: Please login first",
        });
        setShowPopup(true);
        setIsSubmitting(false);
        return;
      }

      // Rest of your existing submission logic...
      if (formData.listingType === "requirement") {
        // Your existing requirement submission logic
        // In the handleSubmit function, update the requirementPayload:
        const requirementPayload = {
          type: formData.type,
          title: formData.title,
          subType: formData.subType,
          brokerName: formData.brokerName,
          contactNumber: formData.contactNumber,
          priceRange: {
            min: parseInt(formData.requirementData.priceRange.min),
            max: formData.requirementData.priceRange.max
              ? parseInt(formData.requirementData.priceRange.max)
              : null, // Set to null if not provided
          },
          areas: formData.requirementData.areas.filter(Boolean),
          bhk: formData.requirementData.bhk,
          sizeRange: {
            sqFt:
              areaUnit === "sqFt"
                ? {
                    min:
                      parseInt(formData.requirementData.sizeRange.sqFt.min) ||
                      0,
                    max: formData.requirementData.sizeRange.sqFt.max
                      ? parseInt(formData.requirementData.sizeRange.sqFt.max)
                      : null, // Set to null if not provided
                  }
                : { min: 0, max: null },
            sqYd:
              areaUnit === "sqYd"
                ? {
                    min:
                      parseInt(formData.requirementData.sizeRange.sqYd.min) ||
                      0,
                    max: formData.requirementData.sizeRange.sqYd.max
                      ? parseInt(formData.requirementData.sizeRange.sqYd.max)
                      : null, // Set to null if not provided
                  }
                : { min: 0, max: null },
          },
          furnishingPreferences: formData.requirementData.furnishingPreferences,
          city: formData.city,
          description: formData.description,
        };

        try {
          const response = await apiClient(
            "POST",
            "/cjidnvij/ceksfbuebijn/user/brokerrequirements/add",
            requirementPayload,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          const respData = response?.data || {};
          const ok = response?.status === 200 || respData?.success === true;

          if (ok) {
            setPopupMessage({
              type: "success",
              message: respData.message || "Requirement posted successfully!",
            });
            setShowPopup(true);
            setFormData(initialFormData);
            setTimeout(() => {
              navigate("/myproperty");
            }, 1200);
          } else {
            setPopupMessage({
              type: "error",
              message:
                respData.error ||
                respData.message ||
                "Failed to submit requirement",
            });
            setShowPopup(true);
          }
        } catch (error) {
          setPopupMessage({
            type: "error",
            message:
              error?.response?.data?.error ||
              error?.message ||
              "Failed to submit requirement",
          });
          setShowPopup(true);
        }
      } else {
        // Your existing non-requirement submission logic
        const uploadData = new FormData();

        // Add all non-media form data
        uploadData.append("listedBy", formData.listingType || "");
        uploadData.append("title", formData.title || "");
        uploadData.append("type", formData.type || "");
        uploadData.append("unitType", formData.subType || "");
        uploadData.append("bhk", formData.bhk[0] || "");
        uploadData.append("furnishedType", formData.furnishing[0] || "");
        uploadData.append("occupencyType", formData.occupencyType || "");
        uploadData.append("sqFt", formData.sqFt || "");
        uploadData.append("sqYd", formData.sqYd || "");
        uploadData.append("age", formData.age || "");
        uploadData.append("floors", formData.floors || "");
        uploadData.append("block", formData.block || "");
        uploadData.append("possessionDate", formData.possessionDate);
        uploadData.append("launchDate", formData.launchDate);
        uploadData.append("reraId", formData.reraId);
        uploadData.append("totalUnit", formData.totalUnit);
        uploadData.append("address", formData.address || "");
        uploadData.append("area", formData.area || "");
        uploadData.append("city", formData.city || "");
        uploadData.append("bathrooms", formData.bathrooms || "");
        uploadData.append("description", formData.description || "");
        uploadData.append("number", formData.contactNumber || "");
        uploadData.append("parking", formData.parking || "");
        uploadData.append("parkingType", formData.parkingType || "");
        uploadData.append("parkingCharge", formData.parkingCharge || "");
        uploadData.append("basicRate", formData.basicRate || "");
        uploadData.append("dpRate", formData.dpRate || "");
        uploadData.append("facing", formData.facing || "");
        uploadData.append("otherFacing", formData.otherFacing || "");
        uploadData.append("amenities", JSON.stringify(formData.amenities));
        const nameToUse =
          formData.listingType === "builders"
            ? formData.builderName
            : formData.brokerName;
        uploadData.append("name", nameToUse || "");
        uploadData.append("developerName", formData.developerName || "");

        if (formData.listingType === "builders") {
          uploadData.append("priceRange[min]", formData.priceMin || 0);
          uploadData.append("priceRange[max]", formData.priceMax || 0);
        } else {
          uploadData.append("price", formData.price || 0);
          uploadData.append("priceRange[min]", formData.price || 0);
          uploadData.append("priceRange[max]", formData.price || 0);
        }

        // Handle media files
        const mediaObjects = [];

        for (let i = 0; i < formData.images.length; i++) {
          const mediaItem = formData.images[i];
          uploadData.append(`media`, mediaItem.file);
          mediaObjects.push({
            type: mediaItem.type,
            name: mediaItem.name,
            index: i,
          });
        }

        uploadData.append("mediaMetadata", JSON.stringify(mediaObjects));

        const response = await apiClient(
          "POST",
          "/cjidnvij/ceksfbuebijn/user/brokerproperties/add",
          uploadData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200) {
          setPopupMessage({
            type: "success",
            message: response.message || "Property listed successfully!",
          });
          setShowPopup(true);
          setFormData(initialFormData);
          setTimeout(() => {
            navigate("/myproperty", { replace: true });
          }, 1000);
        } else {
          setPopupMessage({
            type: "error",
            message:
              response.error ||
              `Error: ${response.status} - ${response.statusText}`,
          });
          setShowPopup(true);
        }
      }
    } catch (error) {
      console.error("Error submitting:", error);
      setPopupMessage({
        type: "error",
        message: error.message || "Something went wrong",
      });
      setShowPopup(true);
    } finally {
      setIsSubmitting(false); // Stop loading regardless of success/error
    }
  };
  return (
    <>
    <AppHeader onMenuToggle={() => { }} />
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-[#503691] px-6 sm:px-8 py-6 sm:py-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-white text-center">
              Add Property Listing
            </h1>
            <p className="text-indigo-100 text-center mt-2 text-sm sm:text-base">
              List your property in just 3 simple steps
            </p>
          </div>

          {/* Progress Bar */}
          <div className="px-6 sm:px-8 py-6">{renderProgressBar()}</div>

          {/* Form Content */}
          <div className="px-6 sm:px-8 pb-6">
            <div className="mb-8">{renderCurrentStep()}</div>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <button
                onClick={handlePrev}
                disabled={currentStep === 0 || isSubmitting}
                className="flex items-center px-4 sm:px-6 py-2 sm:py-3 text-gray-600 hover:text-indigo-600 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                <span className="text-sm sm:text-base">Previous</span>
              </button>

              {currentStep === steps.length - 1 ? (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Listing"
                  )}
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!isStepComplete(currentStep) || isSubmitting}
                  className="flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-[#503691] text-white rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm sm:text-base"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1 sm:ml-2" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading Popup */}
      {isSubmitting && (
        <LoadingPopup message="Submitting your property listing..." />
      )}

      {/* Response Popup */}
      {isResponseOpen && (
        <ResponsePopup
          message={responseMessage}
          type={responseType}
          onClose={() => setIsResponseOpen(false)}
        />
      )}

      {/* Success/Error Popup */}
      {showPopup && (
        <ResponsePopup
          message={popupMessage.message}
          type={popupMessage.type}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
    <ProBrokerFooter />
    </>
  );
}
